import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { Subject, debounceTime, take } from 'rxjs';
import type { Coordinate } from '@sembo-private/maps';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useSuggestionSearchService } from '@apps/main/modules/search/suggestionSearchService';

import type {
  AutoCompleteSuggestionsByCategory,
  SearchSuggestionCriteria,
} from '@libs/trip-planner-interfaces';
import { AutoCompleteSearchType } from '@libs/trip-planner-interfaces';

import {
  AutoCompleteType,
  AUTO_COMPLETE_TYPE_CONFIG,
} from '../AutoComplete.model';

const { t } = i18n.global;
const DEBOUNCE_TIME_IN_MS = 300;

export const useAutoCompleteStore = defineStore('AutoCompleteStore', () => {
  const SEARCH_SERVICE = useSuggestionSearchService();

  // API handling
  const searchTextSubject = new Subject<string>();
  searchTextSubject
    .pipe(debounceTime(DEBOUNCE_TIME_IN_MS))
    .subscribe((newSearchText) => {
      searchSuggestions({
        searchText: newSearchText,
        searchType: autoCompleteSearchType.value,
      });
    });

  // State
  const searchText = ref<string>('');
  const searchPosition = ref<Coordinate | undefined>();
  const autoCompleteType = ref<AutoCompleteType>(AutoCompleteType.Destination);
  const autoCompleteSuggestions = ref<AutoCompleteSuggestionsByCategory[]>([]);

  // Actions
  function initializeAutoComplete(type: AutoCompleteType, text: string): void {
    autoCompleteType.value = type;
    searchText.value = text;
  }

  function setSearchText(newSearchText: string): void {
    searchTextSubject.next(newSearchText);
  }

  function setSearchPosition(newPosition?: Coordinate): void {
    searchPosition.value = newPosition;

    if (newPosition) {
      searchPositionSuggestions(newPosition);
    }
  }

  function searchPositionSuggestions(position: Coordinate): void {
    if (autoCompleteSearchType.value === AutoCompleteSearchType.Flight) {
      searchSuggestions({
        position,
        searchType: autoCompleteSearchType.value,
      });
    }
  }

  function searchSuggestions(criteria: SearchSuggestionCriteria): void {
    if ('searchText' in criteria && criteria.searchText === '') {
      return;
    }

    SEARCH_SERVICE.search(criteria)
      .pipe(take(1))
      .subscribe((response) => (autoCompleteSuggestions.value = response));
  }

  function resetAutoCompleteStore(): void {
    searchText.value = '';
    searchPosition.value = undefined;
    autoCompleteType.value = AutoCompleteType.Destination;
    autoCompleteSuggestions.value = [];
  }

  function cleanAutoCompleteSuggestions(): void {
    autoCompleteSuggestions.value = [];
  }

  function cleanAutoCompleteSuggestionsAndSearchText(): void {
    searchText.value = '';
    autoCompleteSuggestions.value = [];
  }

  // Getters
  const autoCompleteForPosition = computed<boolean>(
    () => AUTO_COMPLETE_TYPE_CONFIG[autoCompleteType.value].isForPosition
  );

  const autoCompletePlaceholder = computed<string>(() =>
    t(AUTO_COMPLETE_TYPE_CONFIG[autoCompleteType.value].translationKey)
  );

  const autoCompleteSearchType = computed<AutoCompleteSearchType>(
    () => AUTO_COMPLETE_TYPE_CONFIG[autoCompleteType.value].searchType
  );

  return {
    // State
    searchText,
    autoCompleteType,

    // Actions
    initializeAutoComplete,
    setSearchText,
    setSearchPosition,
    resetAutoCompleteStore,
    cleanAutoCompleteSuggestions,
    cleanAutoCompleteSuggestionsAndSearchText,

    // Getters
    autoCompleteSuggestions,
    autoCompleteForPosition,
    autoCompletePlaceholder,
  };
});
